import { useState, useContext } from 'react'
import styled from 'styled-components'
import { path, pipe, find, propEq, pathOr } from 'ramda'
import { useTranslation } from 'react-i18next'
import { noop } from 'lodash'
import { colors } from '@/styles'
import arrowIcon from '../dropdown/drop-down-arrow.svg'
import Context from '../context'

const ContentGrid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const GridOne = styled.div`
  font-size: 14px;
  color: ${colors.grey56};
  margin-bottom: 16px;
`

const GridTwo = styled.div`
  text-align: right;
  margin-bottom: 16px;
  min-width: 118px;
`

const FileName = styled.p`
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
  max-width: 100%;
`
const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  width: 100%;
  text-decoration: none;
  cursor: ${props => (props.disableChangePage ? 'not-allowed' : 'pointer')};
`

const CounterWrapper = styled.div`
  display: inline-block;
  text-align: center;
  font-size: 14px;
  width: 52px;
  height: 32px;
  line-height: 32px;
  border: 1px solid ${colors.grey88};
  user-select: none;
`

const Chevron = styled.div`
  display: inline-block;
  text-align: center;
  font-size: 14px;
  width: 32px;
  height: 32px;
  line-height: 32px;
  border: 1px solid ${colors.grey88};
  background-color: ${colors.grey96};
  color: ${colors.grey56};
  user-select: none;
  ${props =>
    !props.disabled &&
    ` 
      background-color: ${colors.white};
      color: ${colors.black};
      cursor: pointer;
      &: hover{
        background-color: ${colors.grey96};
      }

      &: active {
        background-color: ${colors.blue16};
        color: ${colors.white};
      }
  `}
`

const LeftChevron = styled(Chevron)`
  border-radius: 6px 0px 0px 6px;
  border-right: 0;
`

const RightChevron = styled(Chevron)`
  border-radius: 0px 6px 6px 0px;
  border-left: 0;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  ${props =>
    !props.hideBorder &&
    `
      border-top: 1px solid rgba(0, 0, 0, 0.15);
  `}
`

const Image = styled.img`
  width: 116px;
  height: 155px;
  margin: 0 auto;
`
const Icon = styled.img`
  width: 13px;
  height: 9px;
  margin-left: 10px;
  transition: transform 0.3s;
`

const ImageContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  width: 100%;
  margin-bottom: 16px;
  ${props =>
    props.currentPage === 'buildDocuments' &&
    `
      justify-content: center;
  `}
`

const DocumentPageWidget = ({
  file,
  isCurrent = false,
  selectedPage,
  updateSelectedPage,
  stageRef,
  urls,
  currentPage,
  handleClick,
  zoomLevel,
  hideBorder = false,
  currentItems = [],
  signees,
  isEdit = false,
  handleCanvasPageEvent,
  loadNewUrlImage = noop,
  isCreateContract = false
}) => {
  const { t } = useTranslation()
  const userContext = useContext(Context)
  const { fileMetadata, setFileMetadata } = userContext
  const totalPagesCount = urls?.length
  const disableChangePage = isCurrent || isEdit || isCreateContract
  const stage = stageRef?.current?.getStage()
  const [currentThumb, setCurrentThumb] = useState(urls[0])
  const [open, setOpen] = useState(isCurrent)
  const handlePageChange = direction => {
    const value = direction === 'left' ? selectedPage + 1 : selectedPage - 1
    const layer = stage?.find('Layer')
    function handleOpacity(item) {
      if (item.name() === `page-${selectedPage}`) item.hide()
      if (item.name() === `page-${value}`) item.show()
    }
    layer[0].find(`#page-${selectedPage}`)[0].hide()
    layer[0].find(`#page-${value}`)[0].show()
    layer[0].find('Group').forEach(item => {
      handleOpacity(item)
    })
    layer[0].find('Image').forEach(item => {
      handleOpacity(item)
    })

    updateSelectedPage(value)
    setCurrentThumb(urls[value])
  }
  const selectPage = direction => {
    if (direction === 'left') {
      if (selectedPage + 1 <= urls.length - 1) {
        handlePageChange(direction)
      }
    } else {
      if (selectedPage - 1 >= 0) {
        handlePageChange(direction)
      }
    }
  }
  const url = pipe(
    pathOr([], ['package_files']),
    find(propEq('file_id', file?.id)),
    pathOr([], ['location', 0, 0])
  )(fileMetadata)

  const handleChangeDocument = () => {
    if (disableChangePage) return
    setFileMetadata({ ...fileMetadata, current_document: file?.id })
    handleCanvasPageEvent(selectedPage, 0)
    updateSelectedPage(0)
    loadNewUrlImage(url)
    setOpen(true)
  }
  const pageIndex = isCurrent ? selectedPage : 0
  const image = path(['files_metadata', 'images', pageIndex, 'location'], file)
  // const source = getImageUrl(image, {
  //   width: 116,
  //   height: 155
  // })

  const handleZoomOut = () => {
    if (!isCurrent) return
    handleClick('down')
  }

  const handleZoomIn = () => {
    if (!isCurrent) return
    handleClick('up')
  }

  const handlePageBack = () => {
    if (!isCurrent) return
    selectPage('right')
  }

  const handlePageRight = () => {
    if (!isCurrent) return
    selectPage('left')
  }

  return (
    <Wrapper hideBorder={hideBorder}>
      <NameWrapper onClick={handleChangeDocument} disableChangePage={disableChangePage}>
        <FileName>{file?.file_original_name || 'example.pdf'}</FileName>
        {!isCurrent && <Icon visible={open} src={arrowIcon} alt="Toggle Dropdown" />}
      </NameWrapper>
      {open && isCurrent && (
        <>
          <ImageContainer currentPage={currentPage}>
            <Image src={image} />
          </ImageContainer>
          <>
            <ContentGrid>
              <GridOne>{t('l.contract_edit.document_widget.zoom.title', 'Zoom')}</GridOne>
              <GridTwo>
                <LeftChevron onClick={handleZoomOut} disabled={!isCurrent || zoomLevel === 100}>
                  &#45;
                </LeftChevron>
                <CounterWrapper>{`${zoomLevel}%`}</CounterWrapper>
                <RightChevron disabled={!isCurrent || zoomLevel === 200} onClick={handleZoomIn}>
                  &#43;
                </RightChevron>
              </GridTwo>
            </ContentGrid>
            <ContentGrid>
              <GridOne>{t('l.contract_edit.document_widget.page.title', 'Page')}</GridOne>
              <GridTwo>
                <div>
                  <LeftChevron disabled={!isCurrent || selectedPage === 0} onClick={handlePageBack}>
                    &#60;
                  </LeftChevron>
                  <CounterWrapper>
                    {selectedPage + 1} / {totalPagesCount}
                  </CounterWrapper>
                  <RightChevron
                    disabled={!isCurrent || selectedPage == totalPagesCount - 1}
                    onClick={handlePageRight}
                  >
                    &#62;
                  </RightChevron>
                </div>
              </GridTwo>
            </ContentGrid>
          </>
        </>
      )}
    </Wrapper>
  )
}

export default DocumentPageWidget
